import React, {useEffect, useRef, useState} from 'react';
import {useHistory, useLocation} from "react-router-dom";
import '../../assets/css/Home.page.css';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import DOMPurify from "dompurify";
import backgroundImage from '../../assets/images/backgroundImage.jpg';
import Img7 from '../../assets/images/icon7.png';
import Img8 from '../../assets/images/icon8.png';
import Img9 from '../../assets/images/icon9.png';
import GraphIcon1 from '../../assets/images/GraphIcon1.png';
import GraphIcon2 from '../../assets/images/GraphIcon2.png';
import GraphIcon3 from '../../assets/images/GraphIcon3.png';
import GraphIcon4 from '../../assets/images/GraphIcon4.png';
import GraphIcon5 from '../../assets/images/GraphIcon5.png';
import GraphIcon6 from '../../assets/images/GraphIcon6.png';
import GraphIcon7 from '../../assets/images/GraphIcon7.png';
import {
    getCpciIndexDataService,
    getCyberRiskMonitorDataService,
    getGoalsPointsIndexDataService
} from "../../services/elevate_cpc.service";
import {GLOBAL_COUNTRY_CODE,} from "../../utils/constants";
import SunburstHighChart from "../GlobalCPC/SunburstHighChart.comp";

const Content = () => {
    const history = useHistory();
    const location = useLocation()
    const [loading, setLoading] = useState(true);
    const didFetchMonitorData = useRef(false);
    const [displaySunburstRoot, setDisplaySunburstRoot] = useState(false);
    const [selectedNodeData, setSelectedNodeData] = useState(null);
    const [combinedData, setCombinedData] = useState({});
    const didFetchCpciIndexData = useRef(false);
    const didFetchGoalsPointsData = useRef(false);
    const [cpciChartData, setCpciChartData] = useState([]);
    const [cyberRiskMonitorData, setCyberRiskMonitorData] = useState([]);

    const colorMapping = {
        "P1.IndiSkill": "#EBB941",
        "P1.IndiUse": "#00AF9B",
        "P1.Fam": "#F05F64",
        "P1.Sch": "#BE64C3",
        "P1.DESG": "#8269D2",
        "P1.Soft": "#007DB4",
        "P1.Hard": "#003C69",
    };

    const iconMapping = {
        "P1.IndiSkill": GraphIcon1,
        "P1.IndiUse": GraphIcon2,
        "P1.Fam": GraphIcon3,
        "P1.Sch": GraphIcon4,
        "P1.DESG": GraphIcon5,
        "P1.Soft": GraphIcon6,
        "P1.Hard": GraphIcon7,
    };

    useEffect(() => {
        if (didFetchMonitorData.current) return;
        didFetchMonitorData.current = true;
        const fetchCyberRiskMonitorData = async () => {
            try {
                const response = await getCyberRiskMonitorDataService();
                if (response.data.metaData) {
                    console.log('cyberRiskMonitorData: ', response.data.metaData);
                    setCyberRiskMonitorData(response.data.metaData);
                }
            } catch (error) {
                console.error("Error fetching Cyber Risk Monitor data:", error);
            }
        };
        fetchCyberRiskMonitorData().then(r => console.log(r));
    }, []);

    useEffect(() => {
        if (didFetchGoalsPointsData.current) return;
        didFetchGoalsPointsData.current = true;
        getGoalsPointsIndexData().then(r => console.log("Goals Points Data Fetching Started"));
    }, []);

    const getGoalsPointsIndexData = async () => {
        try {
            setLoading(true);
            const response_global = await getGoalsPointsIndexDataService(GLOBAL_COUNTRY_CODE);

            if (response_global?.data?.indexData) {
                const transformedData = Object.keys(response_global.data.indexData)
                    .filter((key) => key.startsWith("P1."))
                    .map((key) => ({
                        name: response_global.data.metaData[key]["IndexL2 (Name)"].toUpperCase(),
                        value: response_global.data.indexData[key],
                        icon: iconMapping[key] || null,
                        color: colorMapping[key] || "#CCCCCC",
                    }));

                setCpciChartData(transformedData);
            }
        } catch (error) {
            console.error("Error fetching CPC Index data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 3000);
        window.scrollTo(0, 0);

        if (location.hash) {
            const hash = location.hash.substring(1);
            const hashTimer = setTimeout(() => {
                const section = document.getElementById(hash);
                if (section) {
                    section.scrollIntoView({behavior: "smooth", block: "start"});
                }
            }, 500);

            return () => {
                clearTimeout(timer);
                clearTimeout(hashTimer);
            };
        }

        return () => clearTimeout(timer);
    }, [location]);


    useEffect(() => {
        if (didFetchCpciIndexData.current) return;
        didFetchCpciIndexData.current = true;
        const fetchCpciData = async () => {
            try {
                const response = await getCpciIndexDataService();
                if (response.data.metaData) {
                    console.log('response.data.metaData: ', response.data.metaData);
                    setCombinedData(response.data.metaData);
                }
            } catch (error) {
                console.error("Error fetching CPC Index data:", error);
            }
        };
        fetchCpciData();
    }, [])

    return (
        <Box className="root">
            {/* Header Section */}
            <Box className="header" sx={{
                marginTop: '80px', background: `url(${backgroundImage}) center/cover no-repeat`,
                backgroundPosition: {
                    xs: '70% center',
                    sm: 'center center',
                },
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
            }}>
                <Box className="headerContent">
                    <Typography sx={{
                        fontSize: {xs: "27px", md: '3rem', lg: "3.6rem"},
                        ml: {lg: '100px'},
                        fontWeight: "bold",
                        lineHeight: '1.2',
                        mb: 2,
                        textTransform: 'uppercase',
                    }}>
                        FROM DATA TO ACTION:<br/>
                        UNIFIED GLOBAL STANDARDS FOR MEASURING CHILD ONLINE PROTECTION
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                textAlign: 'center'
            }}>

                <Box sx={{padding: {xs: '30px 0px 20px 0px', md: '80px 60px 40px 60px', lg: '100px 40px 40px 40px',}}}>
                    <Typography sx={{
                        fontSize: {xs: '23px', sm: '25px', md: '38px',},
                        fontWeight: 'bold',
                        color: '#003C69',
                        textAlign: 'center',
                        marginBottom: 1,
                        px: {xs: '20px', md: '0px',},
                    }}>
                        The CPC Index Framework: A Unified Approach to Child Online Protection
                    </Typography>

                    <Typography sx={{
                        fontSize: {xs: '17px', sm: '25px'},
                        lineHeight: '1.2',
                        color: '#003C69',
                        textAlign: 'left',
                        marginBottom: 4,
                        px: {xs: '20px', md: '0px', lg: '70px'},
                    }}>
                        Protecting children in the AI age is a global priority, but for too long, the data to guide our
                        efforts has been fragmented.
                        Different studies used different definitions, making it difficult to compare progress and build
                        effective, worldwide strategies.
                        The CPC Index Framework was created to solve this problem. We aggregate and harmonize findings
                        from leading global transnational
                        research—including data from the international organizations like the World Health Organizations
                        (WHO), OECD, UNICEF, ITU,
                        alongside trusted industry and academic organizations —to build one unified framework for
                        measuring and advancing child online
                        protection. By synthesizing data from 803, 560 children worldwide, we aim to provide a clear,
                        consistent, and actionable roadmap
                        for every nation.
                    </Typography>

                    <Typography sx={{
                        fontSize: {xs: '22px', sm: '25px', md: '35px',},
                        fontWeight: 'bold',
                        color: '#003C69',
                        textAlign: 'center',
                        marginBottom: 1,
                        px: {xs: '20px', md: '0px',},
                    }}>
                        How It Works: A Structure for Collective Action
                    </Typography>

                    <Typography sx={{
                        fontSize: {xs: '17px', sm: '25px'},
                        lineHeight: '1.2',
                        color: '#003C69',
                        textAlign: 'left',
                        marginBottom: 8,
                        px: {xs: '20px', md: '0px', lg: '70px'},
                    }}>
                        To turn our mission into reality, the framework is built on a clear, hierarchical structure.
                        Every part is connected, ensuring that national
                        policies and local actions all drive toward the same measurable outcomes.
                    </Typography>

                    {/* Points Section */}
                    <Box
                        sx={{
                            textAlign: 'left',
                            px: {xs: '10px', md: '0px', lg: '180px'},
                            mb: 4,
                            ml: 2,
                            mt: 4,
                        }}
                    >
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6} md={3}
                                  sx={{textAlign: 'center', pl: {xs: '20px', sm: undefined}}}>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: {xs: '18px', sm: '30px'},
                                        color: '#007DB3',
                                        mb: 1,
                                        lineHeight: '1.1',
                                    }}
                                >
                                    <span style={{fontSize: '45px'}}>1 </span><br/>Mission
                                </Typography>
                                <Typography
                                    sx={{
                                        lineHeight: '1.1',
                                        fontSize: {xs: '16px', sm: '18px', md: '25px', lg: '20px'},
                                        color: '#003C69',
                                    }}>
                                    An overarching goal for nations and the global community to reduce child cyber risks
                                    by at least 15% in 5 years.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} sx={{textAlign: 'center'}}>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: {xs: '18px', sm: '30px'},
                                        color: '#007DB3',
                                        mb: 1,
                                        lineHeight: '1.1',
                                    }}
                                >
                                    <span style={{fontSize: '45px'}}>3 </span><br/>Goals
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: {xs: '16px', sm: '18px', md: '25px', lg: '20px'},
                                        color: '#003C69',
                                        lineHeight: '1.1',
                                    }}
                                >
                                    Clear objectives spanning all six stakeholders in the child cyber ecosystem to drive
                                    progress toward the Mission.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} sx={{textAlign: 'center'}}>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: {xs: '18px', sm: '30px'},
                                        color: '#007DB3',
                                        mb: 1,
                                        lineHeight: '1.1',
                                    }}
                                >
                                    <span style={{fontSize: '45px'}}>18</span><br/>Targets
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: {xs: '16px', sm: '18px', md: '25px', lg: '20px'},
                                        color: '#003C69',
                                        lineHeight: '1.1',
                                    }}
                                >
                                    Measurable benchmarks for each stakeholder, providing a clear and actionable roadmap
                                    to achieve the Goals.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} sx={{textAlign: 'center'}}>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: {xs: '18px', sm: '30px'},
                                        color: '#007DB3',
                                        mb: 1,
                                        lineHeight: '1.1',
                                    }}
                                >
                                    <span style={{fontSize: '45px'}}>77</span><br/>Indicators
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: {xs: '16px', sm: '18px', md: '25px', lg: '20px'},
                                        color: '#003C69',
                                        lineHeight: '1.1',
                                    }}
                                >
                                    Performance metrics that track progress and measure impact in achieving the Targets.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        px: {xs: '15px', sm: 0},
                        pb: {xs: '15px', md: '30px'}
                    }}>
                        <Button
                            variant="contained"
                            sx={{
                                fontSize: '22px',
                                lineHeight: '1.2',
                                textTransform: 'none',
                                border: '2px solid #003C69',
                                borderRadius: '0px',
                                padding: {xs: '20px 25px', md: '20px 85px'},
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                width: {xs: '90%', md: '55%', lg: '30%'},
                                fontWeight: 'bold',
                                backgroundColor: '#003C69',
                                color: '#fff',
                                mt: 10,
                                mb: {xs: 6, sm: 0},
                                textAlign: 'center',
                                '&:hover': {backgroundColor: '#003C69'},
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '5px'
                            }}

                            onClick={() =>
                                window.open("https://storage.googleapis.com/poweredbydq/resources/2025%20CPC%20Index%20Methodology%20Report.pdf", "_blank"
                                )}>

                            DOWNLOAD THE 2025 CPC INDEX METHODOLOGY REPORT
                        </Button>
                    </Box>
                </Box>

                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: {xs: 'start', lg: 'center'},
                    justifyContent: 'center',
                    padding: {xs: '30px 0px 20px 0px', md: '80px 60px 40px 60px', lg: '40px 40px 40px 40px',}
                }}>
                    <Box sx={{zIndex: 2, color: 'white',}}>
                        <Typography sx={{
                            px: {xs: '20px', sm: undefined},
                            fontSize: {xs: '25px', sm: '45px', md: '38px',},
                            marginTop: {xs: '30px'},
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#003D69',
                        }}>
                            More Than Metrics: A Framework for Action
                        </Typography>
                        <Typography sx={{
                            fontSize: {xs: '17px', sm: '25px'},
                            lineHeight: '1.2',
                            color: '#003C69',
                            textAlign: 'left',
                            marginBottom: 4,
                            px: {xs: '20px', md: '0px', lg: '70px'},
                        }}>
                            The CPC Index provides more than just a single score. Our multi-layered system allows
                            leaders to see the big picture of their nation's performance and then zoom in to analyze
                            progress by goal, stakeholder, and specific targets.
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'block',
                        // height: {xs: undefined, sm: '643px'},
                        width: {xs: '75%', sm: undefined},
                        marginTop: {xs: '30px', sm: '60px'},
                        marginBottom: {xs: '20px', sm: '40px'},
                        marginLeft: {xs: '18%', lg: '10%'},
                        position: 'relative',
                        zIndex: 10
                    }} component="img"
                         src="https://storage.googleapis.com/poweredbydq/assets/images/stakeholders_05.png"
                         alt="Cyber Citizenship"/>
                </Box>

                <Box id="sunburst" sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: "40px",
                    marginBottom: 0,
                    pb: '0px',
                    pr: '0px',
                    pt: {xs: 0, md: 0, lg: 4},
                    pl: {xs: 0, md: 0, lg: 4},
                    // mx: "auto"
                }}>
                    <Box sx={{display: {xs: "block", md: 'block', lg: "flex"}, gap: 2, justifyContent: "center",}}>
                        {/* Left: Sunburst Chart */}
                        <Box sx={{
                            flex: 1,
                            width: {xs: '100%', md: '100%', lg: "55%"},
                            height: {xs: 'auto', md: '100%', lg: 'auto'},
                            display: "flex",
                            flexDirection: 'column',
                            position: "relative",
                            justifyContent: "flex-start",
                            alignItems: {xs: 'center', lg: "center"},
                        }}>
                            <Box sx={{
                                width: '100%',
                                marginBottom: 3,
                                mt: '31px',
                                textAlign: "center",
                                color: "#003C69"
                            }}>
                                <Typography variant="h4" fontWeight="bold" sx={{fontSize: {xs: '23px', md: '40px'},}}>CPC
                                    Index Indicator Navigator</Typography>
                            </Box>
                            <Typography sx={{
                                fontSize: {xs: '17px', sm: '21px'},
                                lineHeight: '1.2',
                                color: '#003C69',
                                textAlign: 'left',
                                marginBottom: 4,
                                px: {xs: '20px', md: '60px', lg: '70px'},
                            }}>
                                Dive into the details that power our framework. The navigator below allows you to
                                explore our 77 evidence-based indicators,
                                including detailed information about assessment items and data sources. These indicators
                                are the foundation of the CPC Index,
                                providing a transparent look at how we measure progress.
                            </Typography>

                            {displaySunburstRoot &&
                                <div className='sunburst-root-label'>
                                    {/*<span>CPC Index Composite Score and Vision</span>*/}
                                </div>
                            }
                            <SunburstHighChart selectedNodeData={selectedNodeData}
                                               setSelectedNodeData={setSelectedNodeData}/>
                        </Box>

                        {/* Right: Information Panel */}
                        <Box sx={{
                            backgroundColor: '#003C69',
                            width: {xs: '100%', md: '100%', lg: "45%"},
                            zIndex: '5',
                            overflowX: {xs: 'hidden', md: 'hidden', lg: 'auto'},
                            p: {xs: '20px', md: '60px', lg: 3},
                            pb: 5,
                            ml: {xs: 0, md: 0, lg: undefined},
                            mr: {xs: 0, md: 0, lg: undefined},
                            border: "2px solid #003C69",
                            borderRadius: "0px",
                            userSelect: "none",
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }}>
                            <Typography variant="h4" fontWeight="bold"
                                        sx={{
                                            fontSize: {xs: '1.5rem', md: '33px', lg: '30px'},
                                            mt: '10px',
                                            color: "#fff",
                                            textAlign: 'center'
                                        }}>
                                {selectedNodeData?.name}
                            </Typography>
                            <Typography
                                sx={{fontSize: {xs: '19px', sm: "25px",}, color: "#fff", textAlign: 'center', mt: 2}}>
                                {selectedNodeData?.displayId}
                            </Typography>
                            <Box sx={{color: "#fff", textAlign: 'left', mx: {xs: 0, lg: '20px'}}}>
                                {selectedNodeData?.desc && (
                                    <Typography sx={{
                                        color: "#fff",
                                        mt: 4,
                                        fontSize: {xs: '17px', sm: "20px"},
                                        textAlign: 'left',
                                        "& a": {
                                            color: "#0196C8",
                                            textDecoration: "underline",
                                        },
                                        "& a:visited": {
                                            color: "#0196C8",
                                        },
                                        "& a:hover": {
                                            color: "#0196C8",
                                        },
                                    }}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        selectedNodeData.type === "indicator" ? `This score reflects <b>${selectedNodeData.desc}</b>` : selectedNodeData.desc
                                                    ),
                                                }}
                                    />
                                )}
                            </Box>

                            {selectedNodeData?.type === "GCCR_indicator" && (
                                <>
                                    <Typography sx={{
                                        color: "#fff",
                                        mt: 2,
                                        fontSize: {xs: '17px', sm: "20px"},
                                        width: '100%',
                                        px: {xs: 0, lg: '20px'},
                                        textAlign: 'left'
                                    }}>
                                        Here is the <b> common definition and measurement</b>  of this cyber-risk indicator:
                                    </Typography>

                                    <TableContainer sx={{
                                        my: 2,
                                        overflowX: {xs: 'hidden', sm: 'auto'},
                                        minHeight: '111px',
                                        border: "1px solid #fff"
                                    }}>
                                        <Table>
                                            {selectedNodeData.definitions?.length > 0 ? (
                                                selectedNodeData.definitions.map((defObj, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell
                                                            sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                fontSize: { xs: "10px", sm: "14px" },
                                                                fontWeight:'bold',
                                                                textAlign:'center',
                                                                minWidth:'155px',
                                                                verticalAlign:'top',
                                                                pt:6,
                                                        }}
                                                        >
                                                            {selectedNodeData.label}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#fff",
                                                                backgroundColor: "#007DB4",
                                                                fontSize: { xs: "10px", sm: "14px" },
                                                                paddingLeft:'0px',
                                                            }}
                                                        >
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: defObj.definition,
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={3}
                                                               sx={{
                                                                   border: "1px solid #003C69",
                                                                   color: "#003C69",
                                                                   backgroundColor: "#007DB4",
                                                                   textAlign: "center",
                                                                   fontSize: { xs: "10px", sm: "14px" },
                                                               }}
                                                    >
                                                        Not available
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                      </Table>
                                    </TableContainer>
                                </>
                            )}

                            {(selectedNodeData?.type === "indicator" || selectedNodeData?.type === "GCCR_indicator") && (
                                <>
                                    <Typography sx={{
                                        color: "#fff",
                                        mt: 2,
                                        fontSize: {xs: '17px', sm: "20px"},
                                        width: '100%',
                                        px: {xs: 0, lg: '20px'},
                                        textAlign: 'left'
                                    }}>
                                        <b>It is developed by aggregating the scores of the following items:</b>
                                    </Typography>
                                    <TableContainer sx={{
                                        my: 2,
                                        overflowX: {xs: 'hidden', sm: 'auto'},
                                        minHeight: '111px',
                                        border: "1px solid #fff"
                                    }}>
                                        <Table>
                                            <TableHead sx={{backgroundColor: "#007DB4"}}>
                                                <TableRow>
                                                    <TableCell sx={{
                                                        fontSize: {xs: '10px', sm: '14px'},
                                                        color: "white",
                                                    }}>Items</TableCell>
                                                    <TableCell
                                                        sx={{fontSize: {xs: '10px', sm: '14px'}, color: "white",}}>Data
                                                        Source</TableCell>
                                                    <TableCell sx={{
                                                        fontSize: {xs: '10px', sm: '14px'},
                                                        color: "white",
                                                    }}>Organization</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {combinedData[selectedNodeData.id]?.dataItem?.length > 0 ? (
                                                    combinedData[selectedNodeData.id]?.dataItem?.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                {item.item}
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                {item.dataSource}
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                {item.organization}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={3} sx={{
                                                            border: "1px solid #003C69",
                                                            color: "#003C69",
                                                            backgroundColor: "#fff",
                                                            textAlign: "center",
                                                            fontSize: {xs: '10px', sm: '14px'}
                                                        }}>
                                                            Not available
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box sx={{color: "#fff", textAlign: 'left', mx: '20px', mt: '20px'}}>
                                        <Typography variant="body1" fontWeight="bold"
                                                    sx={{fontSize: {xs: '17px', md: "20px",}}}>CPC Index Research
                                            Sources</Typography>
                                        <Typography variant="body1" sx={{fontSize: {xs: '17px', md: "20px"},}}>The
                                            following sources contributed to the CPC Index data collection and research
                                            supporting this indicator.</Typography>
                                    </Box>
                                    <TableContainer sx={{
                                        my: 2,
                                        overflowX: {xs: 'hidden', sm: 'auto'},
                                        minHeight: '111px',
                                        border: "1px solid #fff"
                                    }}>
                                        <Table sx={{tableLayout: {xs: 'fixed', sm: 'auto'}}}>
                                            <TableHead sx={{backgroundColor: "#007DB4"}}>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{fontSize: {xs: '10px', sm: '14px'}, color: "white",}}>Research
                                                        Project</TableCell>
                                                    <TableCell
                                                        sx={{fontSize: {xs: '10px', sm: '14px'}, color: "white",}}>Research
                                                        Description</TableCell>
                                                    <TableCell
                                                        sx={{fontSize: {xs: '10px', sm: '14px'}, color: "white",}}>Contribution
                                                        to the CPC Index</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {combinedData[selectedNodeData.id]?.researchProject?.length > 0 ? (
                                                    combinedData[selectedNodeData.id].researchProject.map((project, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#007DB4",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                <a href={project.link} target="_blank"
                                                                   rel="noopener noreferrer"
                                                                   style={{
                                                                       color: "#007DB4",
                                                                       textDecoration: "underline"
                                                                   }}>
                                                                    {project.title}
                                                                </a>
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                {project.description}
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                border: "1px solid #003C69",
                                                                color: "#003C69",
                                                                backgroundColor: "#fff",
                                                                textAlign: "left",
                                                                fontSize: {xs: '10px', sm: '14px'}
                                                            }}>
                                                                {project.contribution}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={3} sx={{
                                                            border: "1px solid #003C69",
                                                            color: "#003C69",
                                                            backgroundColor: "#fff",
                                                            textAlign: "center",
                                                            fontSize: {xs: '10px', sm: '14px'}
                                                        }}>
                                                            Not available
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: "center",
                    flexDirection: 'column',
                    justifyContent: 'center',
                    backgroundColor: '#007DB4',
                    color: 'white',
                    padding: {xs: '50px 20px', sm: '130px 20px', md: '130px 60px', lg: '130px 20px'},
                    textAlign: 'center',
                }}>
                    <Typography sx={{
                        fontSize: {xs: '23px', sm: '40px'},
                        fontWeight: 'bold',
                        marginBottom: '40px',
                        lineHeight: '1.2',
                        maxWidth: {xs: '100%', lg: '60%'},
                        color: '#fff'
                    }}>
                        A CALL FOR COLLABORATION
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: "flex-start",
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor: '#007DB4',
                        color: 'white',
                        textAlign: 'left',
                    }}>
                        <Typography sx={{
                            fontSize: {xs: '17px', sm: '25px'},
                            lineHeight: '1.2',
                            marginBottom: '30px',
                            color: '#fff',
                            textAlign: 'left',
                            px: {xs: '0px', md: '0px', lg: '70px'},
                        }}>
                            One of the biggest global challenges in child online protection is the lack of consistent,
                            standardized data and research—especially in a rapidly evolving technology landscape.
                            Given the global nature of child cyber-risks, the absence of a standardized metric makes it
                            difficult for
                            nations to assess their status and develop effective strategies and action plans.
                        </Typography>

                        <Typography sx={{
                            fontSize: {xs: '20px', sm: '25px'},
                            lineHeight: '1.2',
                            marginBottom: '60px',
                            color: '#fff',
                            textAlign: 'left',
                            px: {xs: '0px', md: '0px', lg: '70px'},
                        }}>
                            The CPC Index was created to solve this, providing a unified, adaptable framework for
                            nations to assess their status and build effective strategies.
                        </Typography>

                        <Typography sx={{
                            fontSize: {xs: '20px', sm: '25px'},
                            lineHeight: '1.2',
                            marginBottom: '60px',
                            color: '#fff',
                            textAlign: 'left',
                            px: {xs: '0px', md: '0px', lg: '70px'},
                        }}>
                            We invite researchers, organizations, and experts to help us continually refine this
                            framework. Your collaboration
                            is vital to ensuring the Index remains dynamic and data-driven in our shared mission to
                            safeguard children online.
                        </Typography>
                    </Box>

                    {/* Cards Container */}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: {xs: '45px', md: '13px', lg: '45px',},
                            flexDirection: {xs: 'column', sm: 'row'},
                        }}
                    >
                        <Card sx={{
                            backgroundColor: '#007DB4',
                            boxShadow: 'none',
                            borderRadius: '0px',
                            minHeight: {xs: '390px', sm: '565px', md: '610px', lg: '505px'},
                            width: {xs: '350px', sm: '250px', md: '300px', lg: '383px',},
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                px: {xs: 0, sm: 'auto',},
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}>
                                <Box
                                    component="img"
                                    src={Img7}
                                    alt="Icon 1"
                                    sx={{
                                        maxHeight: '75px',
                                        display: 'block',
                                        marginTop: '23px',
                                    }}
                                />
                                <Typography sx={{
                                    minHeight: '120px',
                                    fontWeight: 'bold',
                                    marginTop: '30px',
                                    textAlign: 'left',
                                    color: '#fff',
                                    lineHeight: '1.2',
                                    fontSize: {xs: '23px', sm: '25px'}
                                }}>
                                    Measure Nation’s CPC Index Through a Standardized Framework
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: {xs: '17px', sm: '20px'},
                                    lineHeight: '1.3',
                                    color: '#fff',
                                    marginTop: '10px',
                                    textAlign: 'left',
                                }}>
                                    Nations can use CPC Index and its Assessment Toolkit to evaluate Child Protection in
                                    Cyberspace levels across all
                                    key stakeholders against global benchmarks. CPC Index fosters global accountability
                                    by
                                    providing a unified, standardized measurement.
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{
                            backgroundColor: '#007DB4',
                            boxShadow: 'none',
                            borderRadius: '0px',
                            minHeight: {xs: '390px', sm: '565px', md: '610px', lg: '505px'},
                            width: {xs: '350px', sm: '250px', md: '300px', lg: '383px',},
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                px: {xs: 0, sm: 'auto',},
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}>
                                <Box
                                    component="img"
                                    src={Img8}
                                    alt="Icon 2"
                                    sx={{
                                        maxHeight: '80px',
                                        display: 'block',
                                        marginTop: '23px',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    minHeight: '96px',
                                    fontWeight: 'bold',
                                    marginTop: '20px',
                                    color: '#fff',
                                    textAlign: 'left',
                                    lineHeight: '1.2',
                                    fontSize: {xs: '23px', sm: '25px'},
                                    marginBottom: {sm: undefined, md: '25px', lg: undefined,}
                                }}>
                                    Collaborate on Global Research and Co-Create CPC Index
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: {xs: '17px', sm: '20px'},
                                    color: '#fff',
                                    lineHeight: '1.3',
                                    marginTop: '10px',
                                    textAlign: 'left'
                                }}>
                                    Nations can contribute to CPC Index research, shape the index, and collectively
                                    establish
                                    global standards for Child Protection in Cyberspace.
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{
                            backgroundColor: '#007DB4',
                            boxShadow: 'none',
                            borderRadius: '0px',
                            minHeight: {xs: '390px', sm: '565px', md: '610px', lg: '505px'},
                            width: {xs: '350px', sm: '250px', md: '300px', lg: '383px',},
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                px: {xs: 0, sm: 'auto',},
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}>
                                <Box
                                    component="img"
                                    src={Img9}
                                    alt="Icon 3"
                                    sx={{
                                        maxHeight: '70px',
                                        display: 'block',
                                        marginTop: '23px',
                                    }}
                                />
                                <Typography variant="h6" sx={{
                                    textAlign: 'left',
                                    fontWeight: 'bold',
                                    marginTop: '25px',
                                    color: '#fff',
                                    minHeight: '95px',
                                    lineHeight: '1.2',
                                    fontSize: {xs: '23px', sm: '25px'},
                                    marginBottom: {sm: undefined, md: '30px', lg: undefined,}
                                }}>
                                    Share Best Practices and Inspire Action
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: {xs: '17px', sm: '20px'},
                                    color: '#fff',
                                    lineHeight: '1.3',
                                    minHeight: '103px',
                                    marginTop: '10px',
                                    textAlign: 'left'
                                }}>
                                    Nations can showcase their efforts and progress in Child Protection in Cyberspace. CPC Index
                                    will highlight best practices on the international stage, inspiring others to
                                    follow. </Typography>
                            </CardContent>
                        </Card>

                    </Box>
                    <Box sx={{textAlign: 'right', marginTop: '40px'}}>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#003C69',
                                color: '#fff',
                                fontSize: '22px',
                                fontWeight: 'bold',
                                lineHeight: '1.2',
                                textTransform: 'none',
                                border: '2px solid #003C69',
                                borderRadius: '0px',
                                padding: '20px 85px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                    backgroundColor: '#003C69',
                                },
                            }}
                            onClick={() => {
                                history.push('/global_cpc#homeContactSection');
                            }}
                        >
                            JOIN US
                        </Button>
                    </Box>
                </Box>
            </Box>

        </Box>
    );
};

export default Content;